import { Box, SxProps, Theme } from '@mui/material';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';

type AnchorType = 'right' | 'left' | 'top' | 'bottom';

type AnimatedDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
  anchor?: AnchorType;
};

type BackdropProps = {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
};

type ContentProps = {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  anchor?: AnchorType;
};

type DrawerContextType = {
  isOpen: boolean;
  onClose: () => void;
  width: string;
  backdropRef: React.RefObject<HTMLDivElement | null>;
  drawerRef: React.RefObject<HTMLDivElement | null>;
  shouldRender: boolean;
  anchor: AnchorType;
};

const DrawerContext = createContext<DrawerContextType | null>(null);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('Drawer components must be used within AnimatedDrawer');
  }
  return context;
};

const Backdrop = ({ children, sx = {} }: BackdropProps) => {
  const { onClose, backdropRef } = useDrawerContext();

  return (
    <Box
      ref={backdropRef}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        ...sx,
      }}
      onClick={onClose}
    >
      {children}
    </Box>
  );
};

const Content = ({ children, sx = {}, anchor }: ContentProps) => {
  const { width, drawerRef, anchor: contextAnchor } = useDrawerContext();

  const finalAnchor = anchor || contextAnchor;

  const getPositionStyles = (anchor: AnchorType) => {
    const baseStyles = {
      position: 'absolute' as const,
      zIndex: 20,
      backgroundColor: 'background.paper',
      overflow: 'auto',
      borderRadius: '0.5rem',
    };

    switch (anchor) {
      case 'right':
        return {
          ...baseStyles,
          inset: '1rem 1rem 1rem auto',
          width,
          transform: 'translateX(calc(100% + 1rem))',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        };
      case 'left':
        return {
          ...baseStyles,
          inset: '1rem auto 1rem 1rem',
          width,
          transform: 'translateX(calc(-100% - 1rem))',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        };
      case 'top':
        return {
          ...baseStyles,
          inset: '1rem 1rem auto 1rem',
          height: width, // Use width as height for top/bottom
          transform: 'translateY(calc(-100% - 1rem))',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        };
      case 'bottom':
        return {
          ...baseStyles,
          inset: 'auto 1rem 1rem 1rem',
          height: width, // Use width as height for top/bottom
          transform: 'translateY(calc(100% + 1rem))',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        };
    }
  };

  return (
    <Box
      component="aside"
      ref={drawerRef}
      sx={{
        ...getPositionStyles(finalAnchor),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const getAnimationKeyframes = (anchor: AnchorType, isOpening: boolean) => {
  switch (anchor) {
    case 'right':
      return isOpening
        ? [{ transform: 'translateX(calc(100% + 1rem))' }, { transform: 'translateX(0)' }]
        : [{ transform: 'translateX(0)' }, { transform: 'translateX(calc(100% + 1rem))' }];
    case 'left':
      return isOpening
        ? [{ transform: 'translateX(calc(-100% - 1rem))' }, { transform: 'translateX(0)' }]
        : [{ transform: 'translateX(0)' }, { transform: 'translateX(calc(-100% - 1rem))' }];
    case 'top':
      return isOpening
        ? [{ transform: 'translateY(calc(-100% - 1rem))' }, { transform: 'translateY(0)' }]
        : [{ transform: 'translateY(0)' }, { transform: 'translateY(calc(-100% - 1rem))' }];
    case 'bottom':
      return isOpening
        ? [{ transform: 'translateY(calc(100% + 1rem))' }, { transform: 'translateY(0)' }]
        : [{ transform: 'translateY(0)' }, { transform: 'translateY(calc(100% + 1rem))' }];
  }
};

const Root = ({
  isOpen,
  onClose,
  children,
  width = 'clamp(15rem, 25vw, 25rem)',
  anchor = 'right',
}: AnimatedDrawerProps) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const backdropRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const isAnimatingRef = useRef(false);

  useKeyboardShortcuts({
    Escape: () => {
      if (isOpen) {
        onClose();
      }
    },
  });

  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender || isAnimatingRef.current) return;

    const backdropEl = backdropRef.current;
    const drawerEl = drawerRef.current;

    if (!backdropEl || !drawerEl) return;

    isAnimatingRef.current = true;

    if (isOpen) {
      const drawerAnim = drawerEl.animate(getAnimationKeyframes(anchor, true), {
        duration: 300,
        easing: 'cubic-bezier(0, 0.5, 0.5, 1)',
        fill: 'forwards',
      });

      backdropEl.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 200,
        easing: 'cubic-bezier(0, 0.5, 0.5, 1)',
        fill: 'forwards',
      });

      drawerAnim.onfinish = () => {
        isAnimatingRef.current = false;
      };
    } else {
      const drawerAnim = drawerEl.animate(getAnimationKeyframes(anchor, false), {
        duration: 250,
        easing: 'cubic-bezier(0.5, 0, 0, 1)',
        fill: 'forwards',
      });

      backdropEl.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 200,
        easing: 'cubic-bezier(0.5, 0, 0, 1)',
        fill: 'forwards',
      });

      drawerAnim.onfinish = () => {
        setShouldRender(false);
        isAnimatingRef.current = false;
      };
    }
  }, [isOpen, shouldRender, anchor]);

  if (!shouldRender) return null;

  const contextValue: DrawerContextType = {
    isOpen,
    onClose,
    width,
    backdropRef,
    drawerRef,
    shouldRender,
    anchor,
  };

  return <DrawerContext.Provider value={contextValue}>{children}</DrawerContext.Provider>;
};

export const AnimatedDrawer = {
  Root,
  Backdrop,
  Content,
};
